import React from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useTaskProcessingStore,
  selectPendingProcessingTasks,
  selectProcessingTasks,
  isTaskTerminal,
  TERMINAL_TASK_TTL_MS,
} from '../../stores/taskProcessing.store';
import { TaskMonitorItem } from './TaskMonitorItem';

const MAX_TASKS_PER_TICK = 5;
const POLL_TICK_INTERVAL_MS = 3000;

export const TaskMonitor: React.FC = () => {
  const pendingTasks = useTaskProcessingStore(selectPendingProcessingTasks);
  const allTasks = useTaskProcessingStore(selectProcessingTasks);
  const removeTask = useTaskProcessingStore((state) => state.removeTask);
  const queryClient = useQueryClient();
  const queueRef = React.useRef<string[]>([]);
  const terminalTimersRef = React.useRef<Map<string, number>>(new Map());
  const activeRefetchesRef = React.useRef<Set<string>>(new Set());
  const pendingTaskIds = React.useMemo(
    () => pendingTasks.map((task) => task.id),
    [pendingTasks],
  );
  const pendingLookup = React.useMemo(() => {
    const map = new Map<string, (typeof pendingTasks)[number]>();
    pendingTasks.forEach((task) => map.set(task.id, task));
    return map;
  }, [pendingTasks]);

  // Effect 1: Manage polling queue for pending tasks
  React.useEffect(() => {
    const activeIds = new Set(pendingTaskIds);
    queueRef.current = queueRef.current.filter((id) => activeIds.has(id));

    pendingTaskIds.forEach((id) => {
      if (!queueRef.current.includes(id)) {
        queueRef.current.push(id);
      }
    });
  }, [pendingTaskIds]);

  // Effect 2: Auto-cleanup terminal tasks after TTL
  React.useEffect(() => {
    // Clear timers for tasks that no longer exist
    const allTaskIds = new Set(allTasks.map((t) => t.id));
    terminalTimersRef.current.forEach((timerId, taskId) => {
      if (!allTaskIds.has(taskId)) {
        window.clearTimeout(timerId);
        terminalTimersRef.current.delete(taskId);
      }
    });

    // Set cleanup timers for terminal tasks
    allTasks.forEach((task) => {
      if (!isTaskTerminal(task)) {
        // Clear any existing timer for non-terminal tasks
        const existingTimer = terminalTimersRef.current.get(task.id);
        if (existingTimer) {
          window.clearTimeout(existingTimer);
          terminalTimersRef.current.delete(task.id);
        }
        return;
      }

      // Skip if timer already set
      if (terminalTimersRef.current.has(task.id)) {
        return;
      }

      // Set cleanup timer
      const timerId = window.setTimeout(() => {
        removeTask(task.id);
        terminalTimersRef.current.delete(task.id);
      }, TERMINAL_TASK_TTL_MS);

      terminalTimersRef.current.set(task.id, timerId);
    });

    // Cleanup on unmount
    return () => {
      terminalTimersRef.current.forEach((timerId) => {
        window.clearTimeout(timerId);
      });
      terminalTimersRef.current.clear();
    };
  }, [allTasks, removeTask]);

  // Effect 3: Poll pending tasks for status updates
  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    if (pendingTaskIds.length === 0) {
      return undefined;
    }

    const dequeueAndRefetch = () => {
      if (queueRef.current.length === 0) {
        queueRef.current = [...pendingTaskIds];
      }

      const nextBatch = queueRef.current.splice(0, MAX_TASKS_PER_TICK);
      nextBatch.forEach((taskId) => {
        const task = pendingLookup.get(taskId);
        if (!task || isTaskTerminal(task)) {
          return;
        }

        // Skip if already refetching
        if (activeRefetchesRef.current.has(taskId)) {
          return;
        }

        activeRefetchesRef.current.add(taskId);

        queryClient
          .refetchQueries({
            queryKey: ['task-status', taskId],
            exact: true,
          })
          .finally(() => {
            activeRefetchesRef.current.delete(taskId);
          });
      });
    };

    dequeueAndRefetch();

    const intervalId = window.setInterval(
      dequeueAndRefetch,
      POLL_TICK_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, [pendingTaskIds, pendingLookup, queryClient]);

  if (pendingTasks.length === 0) {
    return null;
  }

  return (
    <div className="sr-only" aria-live="polite">
      <ul>
        {pendingTasks.map((task) => (
          <TaskMonitorItem key={task.id} task={task} />
        ))}
      </ul>
    </div>
  );
};

TaskMonitor.displayName = 'TaskMonitor';
